import { isAxiosError } from 'axios';
import API_ENDPOINTS, { QUERY_KEYS } from 'services/urls';
import axiosInstance from 'ns_libs/nsAxios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IDealsResponse, IErrorResponse } from '../types';

export const REFRESH_PRO_FORMA_ACTUALS_MUTATION_KEY = ['refreshProFormaActuals'];

export interface IRefreshActualsVariables {
    organizationId: string;
    dealId: number;
    proFormaId: number;
}

export interface IRefreshActualsResponse {
    netLeveredIrrActuals?: number;
    netLeveredEquityMultipleActuals?: number;
    actualsRefreshedAt?: string;
}

const refreshProFormaActuals = async ({
    organizationId, dealId, proFormaId,
}: IRefreshActualsVariables): Promise<IRefreshActualsResponse> => {
    try {
        const response = await axiosInstance.patch<IRefreshActualsResponse>(
            API_ENDPOINTS.PRO_FORMA_ACTUALS(dealId, proFormaId, organizationId),
        );
        return response.data;
    } catch (error: unknown) {
        if (isAxiosError(error) && error.response && error.response.status !== 500) {
            const errorData: IErrorResponse = error.response.data;
            throw new Error(errorData.detail);
        }
        throw new Error('An unexpected error occurred. Please try again.');
    }
};

export const useRefreshProFormaActuals = () => {
    const queryClient = useQueryClient();

    return useMutation<IRefreshActualsResponse, Error, IRefreshActualsVariables, unknown>({
        mutationKey: REFRESH_PRO_FORMA_ACTUALS_MUTATION_KEY,
        mutationFn: refreshProFormaActuals,
        onSuccess: (data, { organizationId, dealId }) => {
            // Patch the refreshed deal row's actuals in place without a refetch
            queryClient.setQueriesData<IDealsResponse>(
                { queryKey: [QUERY_KEYS.DEALS, organizationId] },
                prev => {
                    if (!prev?.items) return prev;
                    return {
                        ...prev,
                        items: prev.items.map(item => (item.id === dealId
                            ? {
                                ...item,
                                irrProjectActuals: data.netLeveredIrrActuals,
                                equityMultipleProjectActuals: data.netLeveredEquityMultipleActuals,
                                actualsRefreshedAt: data.actualsRefreshedAt,
                            }
                            : item)),
                    };
                },
            );
        },
    });
};
